import { z } from "zod";

import { modifiableElementTypeSchema } from "./fileModifiers";
import { productSchema } from "./product";
import { assetSearchOutputSchema } from "./toolOutput";

const chatMessageRoleSchema = z.enum(["user", "assistant", "system", "tool"]);

export const toolNameSchema = z
  .enum([
    "smallCodeChangeTool",
    "mediumCodeChangeTool",
    "codeChangeSwarmTool",
    "getIntegrationConfigTool",
    "inputProcessorTool",
    "suggestTemplatesTool",
    "generatePageTool",
    "assetSearchTool",
    "getInspirationTool",
    "URLTool",
  ])
  .describe("ToolName");

const chatMessageContentBaseSchema = z.object({
  fileId: z.string().optional(),
  llmRequestId: z.string().optional(),
});

const toolResultBaseSchema = chatMessageContentBaseSchema.extend({
  type: z.literal("tool-result"),
  toolCallId: z.string(),
});

const smallCodeChangeToolResultSchema = toolResultBaseSchema.extend({
  toolName: z.literal("smallCodeChangeTool"),
  output: z.object({
    type: z.literal("json"),
    value: z.object({
      success: z.boolean(),
      isCanceled: z.boolean().optional(),
      appliedDiffs: z.array(z.string()).optional(),
      fileVersionId: z.string().optional(),
      fileId: z.string().optional(),
    }),
  }),
});

const mediumCodeChangeToolResultSchema = toolResultBaseSchema.extend({
  toolName: z.literal("mediumCodeChangeTool"),
  output: z.object({
    type: z.literal("json"),
    value: z.object({
      success: z.boolean(),
      isCanceled: z.boolean().optional(),
      appliedDiffs: z.array(z.string()).optional(),
      fileVersionId: z.string().optional(),
      fileId: z.string().optional(),
    }),
  }),
});

const codeChangeSwarmToolResultSchema = toolResultBaseSchema.extend({
  toolName: z.literal("codeChangeSwarmTool"),
  output: z.object({
    type: z.literal("json"),
    value: z.object({
      success: z.boolean(),
      isCanceled: z.boolean().optional(),
      appliedDiffs: z.array(z.string()).optional(),
      fileVersionId: z.string().optional(),
      fileId: z.string().optional(),
    }),
  }),
});

const getIntegrationConfigToolResultSchema = toolResultBaseSchema.extend({
  toolName: z.literal("getIntegrationConfigTool"),
  output: z.object({
    type: z.literal("json"),
    value: z.object({
      success: z.boolean(),
      isCanceled: z.boolean().optional(),
      message: z.string().optional(),
      integrationConfig: z.any().optional(),
      integrationPrompt: z.string().optional(),
    }),
  }),
});

const inputProcessorToolResultSchema = toolResultBaseSchema.extend({
  toolName: z.literal("inputProcessorTool"),
  output: z.object({
    type: z.literal("json"),
    value: z.object({
      success: z.boolean(),
      isCanceled: z.boolean().optional(),
      message: z.string().optional(),
    }),
  }),
});

export const suggestTemplatesToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("suggestTemplatesTool"),
    output: z.object({
      type: z.literal("json"),
      value: z.object({
        success: z.boolean(),
        isCanceled: z.boolean().optional(),
        suggestedTemplateIds: z.array(z.string()).optional(),
        selectedTemplateId: z.string().optional(),
      }),
    }),
  })
  .describe("SuggestTemplatesToolResult");

export const generatePageToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("generatePageTool"),
    output: z.object({
      type: z.literal("json"),
      value: z.object({
        success: z.boolean(),
        isCanceled: z.boolean().optional(),
        message: z.string().optional(),
        jobId: z.string().optional(),
        publicAccessToken: z.string().optional(),
        createdPageId: z.string().optional(),
        fileVersionId: z.string().optional(),
        triggerJobType: z.string().optional(),
        error: z.string().optional(),
        isCompleted: z.boolean().optional(),
      }),
    }),
  })
  .describe("GeneratePageToolResult");

const assetSearchToolResultSchema = toolResultBaseSchema.extend({
  toolName: z.literal("assetSearchTool"),
  output: z.object({
    type: z.literal("json"),
    value: z.object({
      success: z.boolean(),
      isCanceled: z.boolean().optional(),
      message: z.string().optional(),
      ...assetSearchOutputSchema.shape,
    }),
  }),
});

export const getInspirationToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("getInspirationTool"),
    output: z.object({
      type: z.literal("json"),
      value: z.object({
        success: z.boolean(),
        isCanceled: z.boolean().optional(),
        message: z.string().optional(),
        imageUrl: z.string().optional(),
      }),
    }),
  })
  .describe("GetInspirationToolResult");

const URLToolResultSchema = toolResultBaseSchema.extend({
  toolName: z.literal("URLTool"),
  output: z.object({
    type: z.literal("json"),
    value: z.object({
      success: z.boolean(),
      isCanceled: z.boolean().optional(),
      screenshotUrl: z.string().optional(),
      html: z.string().optional(),
    }),
  }),
});

const toolResultSchema = z.discriminatedUnion("toolName", [
  smallCodeChangeToolResultSchema,
  mediumCodeChangeToolResultSchema,
  codeChangeSwarmToolResultSchema,
  getIntegrationConfigToolResultSchema,
  inputProcessorToolResultSchema,
  suggestTemplatesToolResultSchema,
  generatePageToolResultSchema,
  assetSearchToolResultSchema,
  getInspirationToolResultSchema,
  URLToolResultSchema,
]);

export const toolResultContainingDiffSchema = z
  .discriminatedUnion("toolName", [
    smallCodeChangeToolResultSchema,
    mediumCodeChangeToolResultSchema,
    codeChangeSwarmToolResultSchema,
  ])
  .describe("ToolResultContainingDiff");

export const chatMessageContentSchema = z
  .array(
    z.discriminatedUnion("type", [
      chatMessageContentBaseSchema.extend({
        type: z.literal("text"),
        text: z.string(),
        // NOTE (Bobby: 2025-06-25): It is default text if not provided. This specifies the content inside the text field
        // and alternative content types like products can be parsed into a object like ChatProductItem
        contentType: z
          .enum([
            "text",
            "product",
            "buildError",
            "revert",
            "selectedTemplate",
            "generatedPage",
          ])
          .optional(),
        isHidden: z.boolean().optional(),
        isNewMessageGroup: z.boolean().optional(),
      }),
      chatMessageContentBaseSchema.extend({
        type: z.literal("tool-call"),
        toolCallId: z.string(),
        toolName: toolNameSchema,
        input: z.any(),
      }),
      toolResultSchema,
      chatMessageContentBaseSchema.extend({
        type: z.literal("image"),
        id: z.string().optional(),
        image: z.string(),
        isHidden: z.boolean().optional(),
        isValidChatAsset: z.boolean().optional(),
      }),
    ]),
  )
  .describe("ChatMessageContent");

export const chatMessageSchema = z
  .object({
    id: z.uuid(),
    createdAt: z.coerce.date(),
    role: chatMessageRoleSchema,
    content: chatMessageContentSchema,
    feedback: z.enum(["positive", "negative", "none"]),
    chatId: z.uuid(),
  })
  .describe("ChatMessage");

export const codeChangeToolCallProgressStepSchema = z
  .discriminatedUnion("stepType", [
    z.object({
      id: z.string(),
      stepType: z.literal("applyingChanges"),
      changes: z.object({
        lineCount: z.number(),
      }),
    }),
    z.object({
      id: z.string(),
      stepType: z.literal("applyingSwarmChanges"),
      changes: z.object({
        lineCount: z.number(),
        totalAgents: z.number(),
        agentsCompleted: z.number(),
      }),
    }),
    z.object({
      id: z.string(),
      stepType: z.literal("determiningSwarmSections"),
    }),
    z.object({
      id: z.string(),
      stepType: z.literal("runningFixerAgent"),
      changes: z
        .object({
          lineCount: z.number(),
        })
        .optional(),
    }),
  ])
  .describe("CodeChangeToolCallProgressStep");

export const toolCallProgressSchema = z
  .discriminatedUnion("toolName", [
    z.object({
      toolName: z.literal("codeChangeTool"),
      steps: z.array(codeChangeToolCallProgressStepSchema),
    }),
    z.object({
      toolName: z.literal("codeChangeSwarmTool"),
      steps: z.array(codeChangeToolCallProgressStepSchema),
    }),
  ])
  .describe("ToolCallProgress");

export const chatEventSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("message"),
      data: chatMessageSchema,
    }),
    z.object({
      type: z.literal("tool-progress"),
      data: z.object({
        messageId: z.string(),
        toolCallId: z.string(),
        progress: toolCallProgressSchema,
      }),
    }),
  ])
  .describe("ChatEvent");

export const contextObjectSchema = z
  .object({
    openPageId: z.string().optional().nullable(),
  })
  .describe("ContextObject");

export const chatProductItemSchema = productSchema
  .pick({
    id: true,
    title: true,
    description: true,
    status: true,
    featuredMedia: true,
    media: true,
    options: true,
    variants: true,
    sellingPlanGroups: true,
  })
  .describe("ChatProductItem");

export const chatBuildErrorItemSchema = z
  .object({
    type: z.literal("buildError"),
    isReverting: z.boolean(),
    erroredVersionId: z.uuid().optional(),
    hasRetried: z.boolean().optional(),
    logs: z.string().optional(),
  })
  .describe("ChatBuildErrorItem");

export const chatAssetItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    url: z.string(),
    fileExtension: z.string(),
  })
  .describe("ChatAssetItem");

export const chatRevertItemSchema = z
  .object({
    fileVersionId: z.string(),
    context: z.string().optional(),
  })
  .describe("ChatRevertItem");

export const chatSelectedTemplateItemSchema = z
  .object({
    type: z.literal("selectedTemplate"),
    selectedTemplateId: z.string(),
    selectedTemplateName: z.string(),
  })
  .describe("ChatSelectedTemplateItem");

export const chatGeneratedPageItemSchema = z
  .object({
    type: z.literal("generatedPage"),
    pageName: z.string(),
    pageId: z.string(),
  })
  .describe("ChatGeneratedPageItem");

export const chatTokenSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("product"),
      data: chatProductItemSchema,
    }),
    z.object({
      type: z.literal("asset"),
      data: chatAssetItemSchema,
    }),
    z.object({
      type: z.literal("selected-element"),
      data: z.object({
        elementType: modifiableElementTypeSchema,
        byteOffset: z.number(),
        innerHtml: z.string(),
      }),
    }),
  ])
  .describe("ChatToken");
